import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import { logoutAndRedirect } from '@/lib/auth/logout'
import { RowInner } from './AppNavLinks'
import { HELP_ITEM, PROFILE_ITEM, manageItemsFor } from './navItems'
import { NavGroup } from './navRows'
import appNav from './AppNavLinks.module.css'
import styles from './PublicAccountMenu.module.css'

export function initialsFor(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'ME'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
}

/**
 * THE OPEN PANEL — the same menu on every surface (owner, 2026-09-06: "always,
 * like in the profile"). MANAGE (Workspace for club staff, Admin for a
 * super-admin) over ACCOUNT (Profile + Sign out), gated by the viewer and by
 * nothing else. There used to be a `hideWorkspaceLinks` prop letting a host
 * surface drop MANAGE — the club storefront and the in-app top bar both passed
 * it — and the owner's answer was that the menu should read the same wherever it
 * is opened, so the suppression is gone rather than merely unused.
 *
 * Exported so a test can render the panel itself: the popover only builds its
 * content once opened, and what the panel offers is the part worth holding.
 */
export function AccountMenuPanel({ onClose }: { onClose: () => void }) {
  const { me, isSuperAdmin, isAnyClubStaff } = useAuth()
  const displayName = String(me?.player?.displayName || '').trim() || 'Account'
  // The same signals the sidebar + mobile drawer use, through the one gate.
  const manageItems = manageItemsFor({
    clubStaff: isAnyClubStaff(),
    superAdmin: isSuperAdmin(),
  })

  return (
    <div className={styles.accountMenu} role="menu" aria-label="Account">
      <span className={styles.accountName} aria-hidden="true">
        {displayName}
      </span>
      <hr className={appNav.divider} />

      {manageItems.length > 0 ? (
        <NavGroup label="Manage" items={manageItems} onNavigate={onClose} menu />
      ) : null}

      {/* ACCOUNT — Profile, Help + the Sign out button (same tail idiom as the drawer).
          leadDivider only when Manage sits above it, so a plain player (no
          Manage group) doesn't stack two dividers under the identity header. */}
      <NavGroup
        label="Account"
        leadDivider={manageItems.length > 0}
        items={[PROFILE_ITEM, HELP_ITEM]}
        onNavigate={onClose}
        menu
      >
        <li className={appNav.item} role="none">
          <button
            type="button"
            role="menuitem"
            className={appNav.link}
            onClick={() => {
              onClose()
              void logoutAndRedirect()
            }}
          >
            <RowInner icon="logout" label="Sign out" />
          </button>
        </li>
      </NavGroup>
    </div>
  )
}

/**
 * Signed-in account dropdown — the AVATAR DROPDOWN surface (owner IA 2026-07-22):
 * account + manage only. Shared by the public header modes, the club storefront
 * and the in-app top bar, and identical on all of them. The avatar/initials
 * button is a `role="menu"` trigger; the panel is the SAME grouped, icon'd idiom
 * as the mobile drawer (DrawerNavContent) — it reuses navRows (NavGroup) over
 * AppNavLinks.module.css so desktop and mobile can't drift. The marketing /
 * company menu moved to the horizontal header, so it is no longer here. Closes
 * on route change, ESC, and outside click; ESC returns focus to the trigger.
 */
export function PublicAccountMenu() {
  const { me } = useAuth()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const displayName = String(me?.player?.displayName || '').trim() || 'Account'

  // Close on route change so navigating never leaves the menu hanging open.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  // ESC + outside-click close (only while open). ESC also returns focus to the
  // trigger so keyboard users land back where they opened the menu.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    const onPointer = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onPointer)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  return (
    <div className={styles.accountRoot} ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.avatarButton}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">{initialsFor(displayName)}</span>
      </button>
      {open ? <AccountMenuPanel onClose={() => setOpen(false)} /> : null}
    </div>
  )
}
